<script>
import { mapGetters } from 'vuex';
import { ordinalToLetters } from '@/modules/daiwa-house-modular-europe/ProjectIntake/utils';
import AntInput from '@/components/AntInput.vue';
import { bookOfObjects } from '@/services/bookOf';
import { DHME_PROJECT_INTAKE } from '@/modules/modules';
import { executeCustomModuleCall } from '@/services/api/module.api';
import {
  createRecord,
  deleteRecord,
  deleteRecords,
  importRecords,
  updateRecord,
} from '@/services/api/record.api';
import { queryTablesV2 } from '@/services/api/v2/connect_v2';

const namespace = 'dhmeProjectIntakeStore';

export default {
  name: 'DhmeProjectIntakeLayout',
  components: { AntInput },
  data: () => {
    return {
      isGeneratingBuildNrs: false,
      layerInputs: {},

      buildings: [],
      buildingTableId: null,
      layers: [],
      layerTableId: null,

      projectModules: {
        id: undefined,
        records: [],
      },

      buildingLoading: {},

      focusedBuilding: null,

      buildingFunctionOptions: {
        Woonfunctie: [
          'In een woongebouw',
          'Zorgclusterwoning, zorg op afroep, in een woongebouw',
          'Zorgclusterwoning, 24-uurs zorg, niet in een woongebouw',
          'Zorgclusterwoning, 24-uurs zorg, in een woongebouw',
          'Groepszorgwoning voor zorg op afspraak',
          'Groepszorgwoning voor zorg op afroep',
          'Groepszorgwoning voor 24-uurs zorg',
          'Andere woonfunctie voor zorg',
          'Voor studenten',
          'Voor kamergewijze verhuur',
          'Andere woonfunctie',
        ],
        Celfunctie: ['In een cellengebouw', 'Andere celfunctie'],
        Gezondheidszorgfunctie: [
          'Met bedgebied',
          'Andere gezondheidszorgfunctie',
        ],
        Bijeenkomstfunctie: [
          'Voor kinderopvang voor kinderen < 4 jaar, met bedgebied',
          'Voor kinderopvang voor kinderen < 4 jaar, zonder bedgebied',
          'Andere kinderopvang, met bedgebied',
          'Andere kinderopvang, zonder bedgebied',
          'Andere bijeenkomstfunctie',
          'Voor het aanschouwen van sport',
          'Voor alcoholgebruik',
        ],
        Industriefunctie: [
          'Lichte industriefunctie voor het bedrijfsmatig houden van dieren',
          'Andere lichte industriefunctie',
          'Andere industriefunctie',
        ],
        Kantoorfunctie: ['Kantoorfunctie'],
        Logiesfunctie: [
          'In een logiesgebouw, met 24-uursbewaking',
          'In een logiesgebouw, zonder 24-uursbewaking',
          'Andere logiesfunctie',
        ],
        Onderwijsfunctie: ['Basisonderwijs', 'Andere onderwijsfunctie'],
        Sportfunctie: ['Sportfunctie'],
        Winkelfunctie: ['Winkelfunctie'],
        'Overige gebruiksfunctie': [
          'Voor het personenvervoer',
          'Voor het stallen van motorvoertuigen',
          'Andere overige gebruiksfuncties',
        ],
      },

      updateTimeout: null,
    };
  },

  computed: {
    ...mapGetters({
      masterProject: 'project',
      intakeProject: 'dhmeProjectIntakeStore/selectedProject',
      isDaiwaLicense: 'isDaiwaLicense',
      firm: 'dhmeProjectIntakeStore/firm',
    }),

    buildingFunctions() {
      return Object.keys(this.buildingFunctionOptions);
    },

    buildingSubFunctions() {
      return this.buildingFunctionOptions[this.focusedBuilding?.function];
    },
    moduleId() {
      return this.masterProject.modules.find(
        (module) => module.route === DHME_PROJECT_INTAKE
      ).id;
    },
    totalModuleCount() {
      return this.layers
        .map((x) => x.module_count)
        .reduce((total, count) => total + count, 0);
    },
  },
  async mounted() {
    await this.fetchBuildings();
    await this.getProjectModules();
  },
  methods: {
    async getProjectModules() {
      let { projectModules } = await queryTablesV2({
        tables: [
          {
            name: 'CFFA_DHME_MODULES',
            project: this.intakeProject.id,
            as: 'projectModules',
            columns: [{ name: 'module_id' }, { name: 'build_nr' }],
          },
        ],
      });

      this.projectModules = projectModules;
    },
    async generateModules() {
      this.isGeneratingBuildNrs = true;

      await this.getProjectModules();

      let modules = [];
      let totalCount = this.layers.reduce(
        (total, l) => total + l.module_count,
        0
      );
      let totalIndex = 1;
      this.buildings.forEach((b) => {
        const buildingLayers = this.layers.filter(
          (l) => l.building_id === b.id
        );
        buildingLayers.forEach((l) => {
          for (let i = 1; i <= l.module_count; i++) {
            let obj = { id: null };
            let padded_i = String(i).padStart(
              Math.max(String(totalCount).length, 2),
              '0'
            );
            obj.build_nr = `${this.getColumnLabel(b.ordinal)}${l.layer}.${padded_i}`;
            obj.id =
              this.projectModules.records.find(
                (m) => m.build_nr === obj.build_nr
              )?.id ?? null;
            modules.push(obj);
            totalIndex++;
          }
        });
      });

      const newBuildNumbers = new Set(modules.map((mod) => mod.build_nr));

      const buildNrsToDelete = this.projectModules.records
        .filter((mod) => !newBuildNumbers.has(mod.build_nr))
        .map((mod) => mod.id);

      try {
        // `build_nr` on CFFA_DHME_MODULES is an sbscode column, and the record
        // importer rejects an sbscode value with no matching SBS record in the
        // project. So the SBS tree has to be generated before the module
        // records are imported; the other way around fails with "the sbs code
        // is not found in the project".
        await executeCustomModuleCall(
          this.masterProject.id,
          this.moduleId,
          'generateModulesAndTree',
          {
            modules,
            project: this.intakeProject.id,
          }
        );

        if (buildNrsToDelete.length > 0) {
          await deleteRecords({
            project: {
              id: this.intakeProject.id,
            },
            table: {
              id: this.projectModules.id,
            },
            records: [...buildNrsToDelete],
          });
        }

        let book = bookOfObjects('records', modules);
        let csv = book.convert('csv', 'string');
        let parsedCsv = btoa(unescape(encodeURIComponent(csv)));

        await importRecords({
          project: {
            id: this.intakeProject.id,
          },
          table: {
            id: this.projectModules.id,
          },
          records: parsedCsv,
        });

        await this.getProjectModules();
        this.$store.commit('showNotification', {
          content: `Successfully generated ${modules.length} build numbers`,
          color: 'success',
        });
      } catch (e) {
        this.$store.commit('showNotification', {
          content: e.message,
          color: 'error',
        });
      }

      this.isGeneratingBuildNrs = false;
    },

    getColumnLabel(index) {
      let columnName = '';
      let dividend = index;
      let modulo;

      while (dividend > 0) {
        modulo = (dividend - 1) % 26;
        columnName = String.fromCharCode(65 + modulo) + columnName;
        dividend = parseInt((dividend - modulo) / 26);
      }

      return columnName;
    },
    async addLayerAction(building) {
      this.buildingLoading[building.id] = true;
      try {
        const record = {
          project_id: this.intakeProject.id,
          building_id: building.id,
          module_count: parseInt(this.layerInputs[building.id]),
          layer: this.getLayersByBuilding(building).length,
        };
        const layer = await createRecord({
          project: { id: this.masterProject.id },
          table: { id: this.layerTableId },
          record,
        });
        this.layers.push(layer);

        delete this.layerInputs[building.id];
      } catch (e) {
      } finally {
        this.buildingLoading[building.id] = false;
      }
    },

    async removeLayer(building, layer) {
      try {
        this.buildingLoading[building.id] = true;

        await deleteRecord(layer.id, {
          project: {
            id: this.masterProject.id,
          },
          table: {
            id: this.layerTableId,
          },
        });
        this.layers = this.layers.filter((l) => l.id !== layer.id);

        let updateLayers = this.getLayersByBuilding(building);

        updateLayers.forEach((r, index) => {
          r.layer = index;
          updateRecord(r.id, {
            project: { id: this.masterProject.id },
            table: { id: this.layerTableId },
            record: {
              layer: index,
            },
          });
        });
      } catch (e) {
      } finally {
        this.buildingLoading[building.id] = false;
      }
    },

    async fetchBuildings() {
      const { buildings } = await queryTablesV2({
        tables: [
          {
            name: 'CFFA_DHME_PROJECT_INTAKE_BUILDINGS',
            project: this.masterProject.id,
            as: 'buildings',
            columns: [
              {
                name: 'project_id',
                conditions: [
                  {
                    operator: '=',
                    value: this.intakeProject.id,
                  },
                ],
              },
              ...[
                'construction_system',
                'has_furniture',
                'has_certified_floor',
                'upholstered',
                'exploitation_term',
                'function',
                'rent_quality_grade',
                'fire_resistance',
                'building_surface_area',
                'ordinal',
                'type',
                'name',
                'sub_function',
                'rent_duration',
                'exterior_stairs',
                'project_segment',
                'project_id',
                'system',
                'permit',
                'energy_label',
              ].map((key) => ({ name: key })),
            ],
          },
        ],
      });

      const { layers } = await queryTablesV2({
        tables: [
          {
            name: 'CFFA_DHME_PROJECT_INTAKE_BUILDING_LAYERS',
            project: this.masterProject.id,
            as: 'layers',
            columns: [
              {
                name: 'project_id',
                conditions: [
                  {
                    operator: '=',
                    value: this.intakeProject.id,
                  },
                ],
              },
              {
                name: 'building_id',
                conditions: [
                  {
                    operator: '=',
                    values: buildings.records.map((x) => x.id),
                  },
                ],
              },
              ...['layer', 'module_count'].map((key) => ({ name: key })),
            ],
          },
        ],
      });

      this.buildings = buildings.records;
      this.buildingTableId = buildings.id;

      this.layers = layers.records;
      this.layerTableId = layers.id;
    },
    getLayersByBuilding(building) {
      return this.layers
        .filter((l) => l.building_id === building.id)
        .sort((a, b) => b.layer - a.layer);
    },
    getBuildingModuleCount(building) {
      return this.layers
        .filter((l) => l.building_id === building.id)
        .reduce((a, b) => a + parseInt(b.module_count), 0);
    },
    async addBuilding() {
      try {
        const ordinal = this.buildings
          .map((b) => b.ordinal)
          .reduce((a, b) => Math.max(a, b), 0);
        const building = {
          ordinal: ordinal + 1,
          project_id: this.intakeProject.id,
          name: `Gebouw ${ordinalToLetters(ordinal)}`,
        };

        const record = await createRecord({
          project: { id: this.masterProject.id },
          table: { id: this.buildingTableId },
          record: building,
        });
        this.buildings.push(record);
        this.focusedBuilding = record;
      } catch (e) {
        console.log(e);
      } finally {
      }
    },
    async updateBuilding(key, value) {
      if (this.updateTimeout) clearTimeout(this.updateTimeout);

      this.updateTimeout = setTimeout(async () => {
        try {
          if (this.focusedBuilding) {
            // Save the current focused building state
            const currentBuilding = { ...this.focusedBuilding };

            // Update the record in the database
            await updateRecord(currentBuilding.id, {
              project: { id: this.masterProject.id },
              table: { id: this.buildingTableId },
              record: {
                [key]: value,
              },
            });
          }
        } catch (e) {
          this.$store.commit('showNotification', {
            content: e.message,
            color: 'error',
          });
        } finally {
          this.isLoading = false;
        }
      }, 300); // 300ms debounce to ensure stability
    },
    async setFocusedBuilding(building) {
      if (this.focusedBuilding) {
        // Ensure all updates for the current building are saved
        await this.updateBuilding(); // Pass any pending changes if needed
      }

      // Switch to the newly selected building
      this.focusedBuilding = building;
    },
    async removeBuilding() {
      try {
        await deleteRecord(this.focusedBuilding.id, {
          project: {
            id: this.masterProject.id,
          },
          table: {
            id: this.buildingTableId,
          },
        });
        this.buildings = this.buildings.filter(
          (b) => b.id !== this.focusedBuilding.id
        );
        this.focusedBuilding = null;
      } catch (e) {
        this.$store.commit('showNotification', {
          content: e.message,
          color: 'error',
        });
      } finally {
        this.isLoading = false;
      }
    },
  },
};
</script>

<template>
  <div class="overflow-y-auto pa-5" style="width: calc(100vw - 300px)">
    <form class="d-flex flex-row full-width">
      <div class="d-flex flex-column pr-5" style="flex-basis: 60%">
        <v-btn
          :disabled="
            isGeneratingBuildNrs ||
            totalModuleCount === projectModules.records.length
          "
          :loading="isGeneratingBuildNrs"
          class="mb-5"
          color="primary"
          elevation="0"
          tile
          @click="generateModules"
          >{{
            totalModuleCount === projectModules.records.length
              ? `${totalModuleCount} bouwnummers gegenereerd`
              : 'Genereer bouw nummers'
          }}
        </v-btn>
        <div class="d-flex flex-row flex-shrink-1 flex-wrap">
          <div
            v-for="building in buildings ?? []"
            :key="building.id"
            class="d-flex flex-column pe-2 pb-4 c-pointer"
            style="width: 25%"
            @click="setFocusedBuilding(building)"
          >
            <v-spacer />
            <v-list class="py-0">
              <template v-for="layer in getLayersByBuilding(building)">
                <v-list-item :key="layer.id" class="layer-item" two-line>
                  <div class="font-italic mr-4">{{ layer.layer }}</div>
                  <v-list-item-content>
                    <v-list-item-title>
                      {{ layer.module_count }}
                    </v-list-item-title>
                  </v-list-item-content>
                  <v-list-item-action>
                    <v-btn
                      class="layer-action"
                      icon
                      small
                      @click="removeLayer(building, layer)"
                    >
                      <v-icon dense>mdi-delete</v-icon>
                    </v-btn>
                  </v-list-item-action>
                </v-list-item>
                <v-divider :key="`div_${layer.id}`" />
              </template>
            </v-list>
            <v-card
              :loading="buildingLoading[building.id]"
              :outlined="building.id !== focusedBuilding?.id"
            >
              <v-card-title
                :class="
                  building.id === focusedBuilding?.id &&
                  'text-decoration-underline'
                "
                class="text-truncate"
              >
                {{ building?.name }}
              </v-card-title>
              <v-card-subtitle class="text-truncate">
                <span>
                  {{ getBuildingModuleCount(building) }} modules /
                  {{ building.building_surface_area }} m2
                </span>
              </v-card-subtitle>

              <v-card-actions class="align-end">
                <v-text-field
                  v-model="layerInputs[building.id]"
                  append-icon="mdi-plus"
                  dense
                  filled
                  placeholder="modules"
                  type="number"
                  @keydown.enter="addLayerAction(building)"
                  @click:append="addLayerAction(building)"
                />
              </v-card-actions>
            </v-card>
          </div>

          <div
            class="d-flex flex-column justify-center align-center"
            style="width: 25%"
          >
            <v-btn icon outlined x-large @click="addBuilding">
              <v-icon>mdi-plus</v-icon>
            </v-btn>
          </div>
        </div>
      </div>

      <v-card v-if="focusedBuilding" style="flex-basis: 40%">
        <v-card-title> Gebouw informatie</v-card-title>
        <v-card-text>
          <v-form
            ref="form"
            class="overflow-y-auto overflow-x-hidden"
            style="height: 70vh"
          >
            <v-row>
              <v-col>
                <ant-input label="Gebouw naam" top-margin="mt-0">
                  <template #input-field>
                    <v-text-field
                      v-model="focusedBuilding.name"
                      filled
                      placeholder="Naam"
                      @change="(value) => updateBuilding('name', value)"
                    />
                  </template>
                </ant-input>
              </v-col>
            </v-row>
            <v-row>
              <v-col>
                <ant-input label="Gebouw type" top-margin="mt-0">
                  <template #input-field>
                    <v-select
                      v-model="focusedBuilding.type"
                      :items="['Gebouw', 'Corridor', 'Gallerij']"
                      filled
                      placeholder="Type"
                      @change="(value) => updateBuilding('type', value)"
                    />
                  </template>
                </ant-input>
              </v-col>
              <v-col>
                <ant-input label="Bouwsysteem" top-margin="mt-0">
                  <template #input-field>
                    <v-select
                      v-if="isDaiwaLicense"
                      v-model="focusedBuilding.construction_system"
                      :items="
                        firm !== 'Daiwa House'
                          ? [
                              'K-1000',
                              'K-1500',
                              'K-3000',
                              'K-3000 NAM',
                              'K-3000 BELGIË',
                              'K-3500',
                              'K-4000',
                              'MOVIX 7,5x2,5',
                              'MOVIX 6,0x3,0',
                              'KOMA',
                              'K-1000 (R)',
                              'K-1500 (HL)',
                              'KOMA (HL3)',
                              'KOMA (HL4)',
                              'KOOI CONSTR.',
                              'SALVUS',
                              'SPECIAL',
                              'CONTAINER',
                              'WOONUNIT',
                              'MEUBILAIR',
                              'LOSSE UNIT',
                              'Andere',
                            ]
                          : ['Tijdelijk', 'Permanent']
                      "
                      filled
                      placeholder="Systeem"
                      @change="
                        (value) => updateBuilding('construction_system', value)
                      "
                    />

                    <v-text-field
                      v-else
                      v-model="focusedBuilding.construction_system"
                      filled
                      placeholder="Systeem"
                      @change="
                        (value) => updateBuilding('construction_system', value)
                      "
                    />
                  </template>
                </ant-input>
              </v-col>
            </v-row>
            <v-row>
              <v-col>
                <ant-input label="Functie" top-margin="mt-0">
                  <template #input-field>
                    <v-select
                      v-model="focusedBuilding.function"
                      :items="buildingFunctions"
                      filled
                      placeholder="Functie"
                      @change="(value) => updateBuilding('function', value)"
                    />
                  </template>
                </ant-input>
              </v-col>
              <v-col>
                <ant-input
                  v-if="focusedBuilding?.function"
                  label="Subfunctie"
                  top-margin="mt-0"
                >
                  <template #input-field>
                    <v-select
                      v-model="focusedBuilding.sub_function"
                      :disabled="!focusedBuilding?.function"
                      :items="buildingSubFunctions"
                      filled
                      placeholder="Sub functie"
                      @change="(value) => updateBuilding('sub_function', value)"
                    />
                  </template>
                </ant-input>
              </v-col>
            </v-row>
            <v-row>
              <v-col>
                <ant-input label="Systeem" top-margin="mt-0">
                  <template #input-field>
                    <v-select
                      v-model="focusedBuilding.system"
                      :items="[
                        'DTNL 5lg',
                        'DTNL boven 5lg',
                        'DPNL 5lg',
                        'DPNL boven 5lg',
                        'Speciaal',
                      ]"
                      filled
                      placeholder="Systeem"
                      @change="(value) => updateBuilding('system', value)"
                    />
                  </template>
                </ant-input>
              </v-col>
              <v-col>
                <ant-input label="Project Segment" top-margin="mt-0">
                  <template #input-field>
                    <v-select
                      v-model="focusedBuilding.project_segment"
                      :items="[
                        'Gezondheidszorg',
                        'Industrie',
                        'Onderwijs',
                        'Kantoren',
                        'Huisvesting',
                        'Overheid',
                        'Overig',
                      ]"
                      filled
                      placeholder="Segment"
                      @change="
                        (value) => updateBuilding('project_segment', value)
                      "
                    />
                  </template>
                </ant-input>
              </v-col>
            </v-row>
            <v-row>
              <v-col>
                <ant-input label="Energie Label" top-margin="mt-0">
                  <template #input-field>
                    <v-select
                      v-model="focusedBuilding.energy_label"
                      :items="['Ja', 'Nee']"
                      filled
                      placeholder="Ja of Nee"
                      @change="(value) => updateBuilding('energy_label', value)"
                    />
                  </template>
                </ant-input>
              </v-col>
            </v-row>
            <v-row>
              <v-col>
                <ant-input label="Gebouw oppervlakte" top-margin="mt-0">
                  <template #input-field>
                    <v-text-field
                      v-model="focusedBuilding.building_surface_area"
                      filled
                      placeholder="Aantal vierkantemeters"
                      suffix="m2"
                      type="number"
                      @change="
                        (value) =>
                          updateBuilding('building_surface_area', value)
                      "
                    />
                  </template>
                </ant-input>
              </v-col>
              <v-col>
                <ant-input label="Gemeubileerd" top-margin="mt-0">
                  <template #input-field>
                    <v-checkbox
                      v-model="focusedBuilding.has_furniture"
                      filled
                      @change="
                        (value) => updateBuilding('has_furniture', value)
                      "
                    />
                  </template>
                </ant-input>
              </v-col>
            </v-row>
            <v-row>
              <v-col>
                <ant-input label="Buiten trappen" top-margin="mt-0">
                  <template #input-field>
                    <v-text-field
                      v-model="focusedBuilding.exterior_stairs"
                      filled
                      placeholder="Aantal"
                      type="number"
                      @change="
                        (value) => updateBuilding('exterior_stairs', value)
                      "
                    />
                  </template>
                </ant-input>
              </v-col>
              <v-col>
                <ant-input label="Brandwerendheid" top-margin="mt-0">
                  <template #input-field>
                    <v-select
                      v-model="focusedBuilding.fire_resistance"
                      :items="['NVT', '30 min', '60 min', '90 min', '120 min']"
                      filled
                      placeholder="In minuten"
                      @change="
                        (value) => updateBuilding('fire_resistance', value)
                      "
                    />
                  </template>
                </ant-input>
              </v-col>
            </v-row>
            <v-row>
              <v-col>
                <ant-input label="Huur duur" top-margin="mt-0">
                  <template #input-field>
                    <v-text-field
                      v-model="focusedBuilding.rent_duration"
                      filled
                      placeholder="Aantal weken"
                      suffix="weken"
                      type="number"
                      @change="
                        (value) => updateBuilding('rent_duration', value)
                      "
                    />
                  </template>
                </ant-input>
              </v-col>
              <v-col>
                <ant-input label="Huur kwaliteits graad" top-margin="mt-0">
                  <template #input-field>
                    <v-select
                      v-model="focusedBuilding.rent_quality_grade"
                      :items="['6', '7', '8', '9', '10', 'NVT']"
                      filled
                      placeholder="Graad"
                      @change="
                        (value) => updateBuilding('rent_quality_grade', value)
                      "
                    />
                  </template>
                </ant-input>
              </v-col>
            </v-row>
          </v-form>
        </v-card-text>

        <v-card-actions class="ant-border-top ant-dialog-actions-bg">
          <v-spacer />
          <v-btn
            color="primary"
            elevation="0"
            small
            @click.stop="removeBuilding"
          >
            <v-icon class="mr-2" dense>mdi-delete-alert-outline</v-icon>
            Verwijder
          </v-btn>
        </v-card-actions>
      </v-card>
    </form>
  </div>
</template>

<style lang="scss">
.layer-item {
  .layer-action {
    opacity: 0;
  }

  &:hover {
    .layer-action {
      opacity: 1;
    }
  }
}
</style>
